/**
 * React binding for VavClient, shared by the web and mobile apps.
 *
 * The client is the source of truth; this hook mirrors its events into React
 * state. Message lists are kept per-conversation and only for the open thread,
 * so a long history does not sit in memory.
 *
 * Storage and the WebSocket constructor are injected because they are the only
 * two things that genuinely differ between the platforms.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VavClient } from './client.ts';
import type { ConnectionStatus, WebSocketFactory } from './socket.ts';
import type { Contact, Conversation, Message } from './model.ts';
import type { KeyValueStore } from './storage.ts';
import type { AttachmentMeta } from '@vav/protocol';

export interface IdentityAlert {
  userId: string;
  newIdentityKey: string;
}

/**
 * `failed` exists because the alternative is a spinner that never stops.
 *
 * Boot touches the keystore, the database and the network, any of which can
 * fail on a real device. Without a terminal failure state those errors leave
 * the app on its loading screen forever, showing nothing and logging nothing —
 * which is indistinguishable from a hang and impossible to report usefully.
 */
export type Phase = 'loading' | 'onboarding' | 'ready' | 'failed';

export interface UseVavOptions {
  serverUrl: string;
  /** Invite code lifted from the URL, if the user arrived through a link. */
  inviteCode?: string;
  /** Called once on mount to build the platform's persistent store. */
  createStore: () => Promise<KeyValueStore>;
  webSocketFactory?: WebSocketFactory;
}

export function useVav({ serverUrl, createStore, webSocketFactory, inviteCode }: UseVavOptions) {
  const [client, setClient] = useState<VavClient>();
  const [phase, setPhase] = useState<Phase>('loading');
  const [status, setStatus] = useState<ConnectionStatus>('offline');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<string>();
  const [messages, setMessages] = useState<Message[]>([]);
  const [typing, setTyping] = useState<Record<string, string[]>>({});
  const [presence, setPresence] = useState<Record<string, boolean>>({});
  const [identityAlerts, setIdentityAlerts] = useState<IdentityAlert[]>([]);
  const [error, setError] = useState<string>();
  const [, setAccountVersion] = useState(0);
  const [contactsVersion, setContactsVersion] = useState(0);
  const [inviter, setInviter] = useState<{ displayName: string; username: string }>();

  // React 19 requires an explicit initial value: `useRef<T>()` no longer
  // implies undefined. Widening the type is the honest fix -- the ref really
  // does start empty.
  const activeIdRef = useRef<string | undefined>(undefined);
  activeIdRef.current = activeId;

  // ---------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const store = await createStore();
      const instance = new VavClient({ serverUrl, store, webSocketFactory });
      const restored = await instance.load();
      if (cancelled) return;

      if (restored) {
        try {
          // The stored token may have expired while the app was closed.
          await instance.login();
        } catch (err) {
          console.warn('[vav] refresh failed, using the stored token', err);
        }
        await instance.connect();
      }
      setClient(instance);
      setConversations(instance.conversations);
      setPhase(restored ? 'ready' : 'onboarding');
    })().catch((err) => {
      // Previously absent, and the reason a failed boot looked like a hang: an
      // unhandled rejection here skips every setState below it, so the app sat
      // on its splash with no error on screen and none in the log.
      if (cancelled) return;
      console.error('[vav] boot failed', err);
      setError((err as Error).message ?? String(err));
      setPhase('failed');
    });

    return () => {
      cancelled = true;
    };
  }, [serverUrl]);

  // Resolve who invited us, so onboarding can greet them by name.
  useEffect(() => {
    if (!client || !inviteCode || phase !== 'onboarding') return;
    void client.resolveInvite(inviteCode).then((found) => found && setInviter(found));
  }, [client, inviteCode, phase]);

  // ---------------------------------------------------------------------
  // Wire up events
  // ---------------------------------------------------------------------

  useEffect(() => {
    if (!client) return;

    const offs = [
      client.on('status', setStatus),
      client.on('conversation', () => setConversations(client.conversations)),
      client.on('error', (err) => setError(err.message)),
      client.on('identity-change', (alert) =>
        setIdentityAlerts((prev) =>
          prev.some((a) => a.userId === alert.userId) ? prev : [...prev, alert],
        ),
      ),
      client.on('message', (message) => {
        setConversations(client.conversations);
        if (message.conversationId !== activeIdRef.current) return;
        setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
        // The thread is open, so anything arriving in it is already read.
        if (!message.outgoing) void client.markRead(message.conversationId);
      }),
      client.on('message-updated', (message) => {
        setConversations(client.conversations);
        if (message.conversationId !== activeIdRef.current) return;
        setMessages((prev) => prev.map((m) => (m.id === message.id ? message : m)));
      }),
      /**
       * The account object is read straight off the client by several
       * components, so a change to it has to force a render here or the old
       * value stays on screen.
       */
      client.on('account', () => setAccountVersion((v) => v + 1)),
      client.on('contact', () => setContactsVersion((v) => v + 1)),
      client.on('contact-removed', () => setContactsVersion((v) => v + 1)),
      client.on('conversation-removed', ({ conversationId }) => {
        setConversations(client.conversations);
        // A conversation that no longer exists must not stay on screen.
        if (conversationId === activeIdRef.current) setActiveId(undefined);
      }),
      client.on('message-removed', ({ conversationId, messageId }) => {
        setConversations(client.conversations);
        if (conversationId !== activeIdRef.current) return;
        setMessages((prev) => prev.filter((m) => m.id !== messageId));
      }),
      client.on('typing', ({ conversationId, userId, isTyping }) => {
        setTyping((prev) => {
          const current = new Set(prev[conversationId] ?? []);
          if (isTyping) current.add(userId);
          else current.delete(userId);
          return { ...prev, [conversationId]: [...current] };
        });
      }),
      client.on('presence', ({ userId, online }) =>
        setPresence((prev) => ({ ...prev, [userId]: online })),
      ),
    ];

    return () => offs.forEach((off) => off());
  }, [client]);

  // ---------------------------------------------------------------------
  // Load a thread when it opens
  // ---------------------------------------------------------------------

  useEffect(() => {
    if (!client || !activeId) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const history = await client.loadMessages(activeId);
      if (cancelled) return;
      setMessages(history);
      await client.markRead(activeId);
      setConversations(client.conversations);
    })();
    return () => {
      cancelled = true;
    };
  }, [client, activeId]);

  // ---------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------

  /**
   * `phone` is optional and is attached *after* the account exists, never sent
   * to `register`. Signup asks for it because being findable from day one is
   * worth a field, but it stays a discovery attribute: it is not the account
   * name, not a credential, and not how the account is recovered. Making it any
   * of those would turn a SIM swap into account takeover.
   *
   * A rejected number therefore cannot fail the signup. The account is already
   * created and usable at that point, so the number is reported and dropped
   * rather than unwinding anything -- and it can be set later from settings.
   */
  const register = useCallback(
    async (username: string, displayName: string, phone?: string) => {
      if (!client) return;
      setError(undefined);
      await client.register(username, displayName, inviteCode);

      if (phone?.trim()) {
        try {
          await client.setDiscoverablePhone(phone.trim());
        } catch (err) {
          setError((err as Error).message);
        }
      }

      await client.connect();
      setConversations(client.conversations);
      setPhase('ready');
    },
    [client],
  );

  const sendText = useCallback(
    async (text: string, replyTo?: string) => {
      if (!client || !activeId) return;
      await client.sendText(activeId, text, replyTo);
    },
    [client, activeId],
  );

  /**
   * Bytes rather than a `File`: React Native has no File, and each platform
   * gets the raw data from a different picker.
   */
  const sendMedia = useCallback(
    async (bytes: Uint8Array, meta: AttachmentMeta, caption?: string) => {
      if (!client || !activeId) return;
      await client.sendMedia(activeId, bytes, meta, caption);
    },
    [client, activeId],
  );

  const activeConversation = useMemo(
    () => conversations.find((c) => c.id === activeId),
    [conversations, activeId],
  );

  const contactsById = useMemo(() => {
    const map: Record<string, Contact> = {};
    for (const contact of client?.contacts ?? []) map[contact.id] = contact;
    return map;
    // contactsVersion is the point: a renamed contact changes no conversation,
    // so without it this memo kept serving the old name for ever.
  }, [client, conversations, contactsVersion]);

  const dismissIdentityAlert = useCallback(
    async (userId: string, accept: boolean) => {
      if (accept) await client?.acceptIdentityChange(userId);
      setIdentityAlerts((prev) => prev.filter((a) => a.userId !== userId));
    },
    [client],
  );

  return {
    client,
    phase,
    status,
    conversations,
    activeId,
    activeConversation,
    setActiveId,
    messages,
    typing: activeId ? (typing[activeId] ?? []) : [],
    presence,
    contactsById,
    identityAlerts,
    dismissIdentityAlert,
    error,
    inviter,
    clearError: () => setError(undefined),
    register,
    sendText,
    sendMedia,
  };
}

export type UseVavResult = ReturnType<typeof useVav>;
